import { useLoaderData } from "@tanstack/react-router";
import {
  fetchCollections,
  storefrontApiRequest,
  type ShopifyCollection,
} from "@/lib/shopify";
import { navGroups } from "@/lib/navigation";
import { useT } from "@/lib/i18n";
import { LocaleLink } from "@/components/LocaleLink";

type CollectionWithFallback = ShopifyCollection & { fallbackImage?: string | null };

export const collectionsIndexLoader = async () => {
  const collections = await fetchCollections();

  // For any collection missing a hero image, pull the first product image as
  // a fallback so the grid never shows a blank tile with just an initial.
  const missing = collections.filter((c) => !c.image?.url);
  if (missing.length > 0) {
    const query = `query Fallbacks(${missing
      .map((_, i) => `$h${i}: String!`)
      .join(", ")}) {
      ${missing
        .map(
          (_, i) => `c${i}: collection(handle: $h${i}) {
            products(first: 1) { edges { node { images(first: 1) { edges { node { url } } } } } }
          }`,
        )
        .join("\n")}
    }`;
    const variables: Record<string, string> = {};
    missing.forEach((c, i) => (variables[`h${i}`] = c.handle));
    const res = await storefrontApiRequest<Record<string, { products: { edges: Array<{ node: { images: { edges: Array<{ node: { url: string } }> } } }> } } | null>>(
      query,
      variables,
    );
    const data = res?.data ?? {};
    const enriched: CollectionWithFallback[] = collections.map((c) => {
      if (c.image?.url) return c;
      const idx = missing.indexOf(c);
      const node = data?.[`c${idx}`];
      const url = node?.products?.edges?.[0]?.node?.images?.edges?.[0]?.node?.url ?? null;
      return { ...c, fallbackImage: url };
    });
    return { collections: enriched };
  }

  return { collections: collections as CollectionWithFallback[] };
};

export const collectionsIndexHeadDa = () => ({
  meta: [
    { title: "Kollektioner — Farveladeland" },
    {
      name: "description",
      content:
        "Browse alle Farveladelands kollektioner: vintage Murano-glas, lysekroner, bryllupsservice, glas, lamper og udvalgte loppefund.",
    },
    { property: "og:title", content: "Kollektioner — Farveladeland" },
    {
      property: "og:description",
      content: "Vintage Murano-glas og udvalgte loppefund, sendt over hele verden.",
    },
  ],
});

export const collectionsIndexHeadEn = () => ({
  meta: [
    { title: "Collections — Farveladeland" },
    {
      name: "description",
      content:
        "Browse all of Farveladeland's collections: vintage Murano glass, chandeliers, wedding service, glassware, lamps and curated flea-market finds.",
    },
    { property: "og:title", content: "Collections — Farveladeland" },
    {
      property: "og:description",
      content: "Vintage Murano glass and curated flea-market finds, shipped worldwide.",
    },
  ],
});

export function CollectionsIndex() {
  const { collections } = useLoaderData({ strict: false }) as { collections: CollectionWithFallback[] };
  const { t, locale } = useT();
  const byHandle = new Map(collections.map((c) => [c.handle, c]));

  return (
    <>
      <section className="pt-40 pb-12 md:pt-52 md:pb-16">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10">
          <p className="eyebrow">{t("collections.eyebrow")}</p>
          <h1 className="mt-4 font-display text-[clamp(3rem,7vw,6rem)] leading-[0.95]">
            {t("collections.titleA")}
            <br />
            <em className="text-terracotta">{t("collections.titleB")}</em>
          </h1>
          <p className="mt-6 max-w-xl text-pretty text-base leading-relaxed text-muted-foreground">
            {t("collections.body", { n: collections.length })}
          </p>
          <div className="mt-8">
            <LocaleLink
              to="/alle"
              className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-background hover:bg-terracotta"
            >
              {t("collections.browseAll")}
            </LocaleLink>
          </div>
        </div>
      </section>


      <section className="mx-auto max-w-[1500px] space-y-24 px-6 pb-32 md:px-10">
        {navGroups
          .filter((g) => g.labelKey !== "nav.sale")
          .map((group) => {
            const groupCols = group.collections
              .map((c) => byHandle.get(c.handle))
              .filter(Boolean) as CollectionWithFallback[];
            if (groupCols.length === 0) return null;

            return (
              <div key={group.labelKey}>
                <div className="mb-10 flex items-end justify-between border-b border-border pb-4">
                  <div>
                    <p className="eyebrow">{group.italian}</p>
                    <h2 className="mt-2 font-display text-3xl md:text-4xl">
                      {t(group.labelKey)}
                    </h2>
                  </div>
                  <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                    {t("collections.count", { n: groupCols.length })}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-10 sm:grid-cols-3 lg:grid-cols-4">
                  {groupCols.map((c) => {
                    const navEntry = group.collections.find((x) => x.handle === c.handle);
                    const label = navEntry
                      ? locale === "da"
                        ? navEntry.labelDa
                        : navEntry.labelEn
                      : c.title;
                    return (
                      <LocaleLink
                        key={c.handle}
                        to="/collections/$handle"
                        params={{ handle: c.handle }}
                        className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-4 focus-visible:ring-offset-background"
                      >
                        <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-muted">
                          {(() => {
                            const src = c.image?.url ?? c.fallbackImage ?? null;
                            return src ? (
                              <img
                                src={src}
                                alt={c.image?.altText ?? label}
                                loading="lazy"
                                decoding="async"
                                className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-gradient-warm">
                                <span className="font-display text-3xl text-foreground/30">
                                  {c.title.charAt(0)}
                                </span>
                              </div>
                            );
                          })()}
                          <div className="absolute inset-0 bg-gradient-to-t from-foreground/55 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                        </div>
                        <h3 className="mt-4 font-display text-lg leading-snug text-foreground group-hover:text-terracotta">
                          {label}
                        </h3>
                      </LocaleLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </section>
    </>
  );
}
